import React, { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaClock,
  FaWhatsapp,
  FaFacebookF,
  FaTwitter,
  FaYoutube,
  FaInstagram,
  FaLinkedinIn,
  FaUser,
} from "react-icons/fa";

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
  };

  const socialLinks = [
    {
      icon: <FaFacebookF />,
      url: "#",
      color: "hover:bg-blue-600",
      name: t("contact.social.facebook"),
    },
    {
      icon: <FaTwitter />,
      url: "#",
      color: "hover:bg-sky-500",
      name: t("contact.social.twitter"),
    },
    {
      icon: <FaYoutube />,
      url: "#",
      color: "hover:bg-red-600",
      name: t("contact.social.youtube"),
    },
    {
      icon: <FaInstagram />,
      url: "#",
      color: "hover:bg-pink-600",
      name: t("contact.social.instagram"),
    },
    {
      icon: <FaLinkedinIn />,
      url: "#",
      color: "hover:bg-blue-700",
      name: t("contact.social.linkedin"),
    },
  ];

  // Animated fruit emojis for background
  const floatingFruits = [
    { emoji: "🍌", delay: 0, duration: 20, x: "10%", y: "15%" },
    { emoji: "🥭", delay: 2, duration: 25, x: "80%", y: "20%" },
    { emoji: "🍍", delay: 4, duration: 22, x: "15%", y: "70%" },
    { emoji: "🥥", delay: 1, duration: 23, x: "85%", y: "75%" },
    { emoji: "🍌", delay: 3, duration: 24, x: "50%", y: "10%" },
    { emoji: "🥭", delay: 5, duration: 21, x: "30%", y: "50%" },
    { emoji: "🍍", delay: 2.5, duration: 26, x: "70%", y: "50%" },
    { emoji: "🥥", delay: 4.5, duration: 19, x: "90%", y: "40%" },
    { emoji: "🍌", delay: 1.5, duration: 23, x: "5%", y: "45%" },
    { emoji: "🥭", delay: 3.5, duration: 22, x: "95%", y: "60%" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-green-100 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 relative overflow-hidden">
      {/* Animated Wave Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-30">
        <svg
          className="absolute w-full h-full"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient
              id="wave-gradient"
              x1="0%"
              y1="0%"
              x2="100%"
              y2="100%"
            >
              <stop
                offset="0%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
              <stop
                offset="50%"
                style={{ stopColor: "#3b82f6", stopOpacity: 0.2 }}
              />
              <stop
                offset="100%"
                style={{ stopColor: "#10b981", stopOpacity: 0.2 }}
              />
            </linearGradient>
          </defs>
          <motion.path
            d="M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z"
            fill="url(#wave-gradient)"
            animate={{
              d: [
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,150 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
                "M0,100 Q250,50 500,100 T1000,100 T1500,100 T2000,100 V200 H0 Z",
              ],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
          <motion.path
            d="M0,150 Q250,100 500,150 T1000,150 T1500,150 T2000,150 V300 H0 Z"
            fill="url(#wave-gradient)"
            animate={{
              d: [
                "M0,150 Q250,100 500,150 T1000,150 T1500,150 T2000,150 V300 H0 Z",
                "M0,150 Q250,200 500,150 T1000,150 T1500,150 T2000,150 V300 H0 Z",
                "M0,150 Q250,100 500,150 T1000,150 T1500,150 T2000,150 V300 H0 Z",
              ],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
              delay: 1,
            }}
          />
        </svg>
      </div>

      {/* Animated Fruit Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {floatingFruits.map((fruit, index) => (
          <motion.div
            key={index}
            className="absolute"
            style={{ left: fruit.x, top: fruit.y }}
            animate={{
              y: [0, -40, 0],
              x: [0, 20, 0],
              rotate: [0, 360],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: fruit.duration,
              repeat: Infinity,
              delay: fruit.delay,
              ease: "easeInOut",
            }}
          >
            <div className="text-6xl md:text-8xl opacity-20 drop-shadow-lg">
              {fruit.emoji}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Misty Overlay */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-white/50 via-transparent to-white/30 dark:from-dark-900/50 dark:via-transparent dark:to-dark-900/30"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-tl from-green-100/30 via-transparent to-blue-100/30 dark:from-green-900/30 dark:via-transparent dark:to-blue-900/30"
          animate={{
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 2,
          }}
        />
      </div>

      {/* Main Content */}
      <div className="relative z-10">
        {/* Section 1: Map, Form, Contact Info */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <div className="grid lg:grid-cols-12 gap-6">
              {/* Left: Google Map */}
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-4"
              >
                <div className="rounded-2xl shadow-2xl overflow-hidden h-full">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3960.686454165752!2d80.05121577448277!3d6.928031118331305!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3ae255913b50bf17%3A0x18a8733693f0943!2sWorga%20Naturals%20(Pvt)%20Ltd!5e0!3m2!1sen!2suk!4v1761773411185!5m2!1sen!2suk"
                    width="100%"
                    height="100%"
                    style={{ border: 0, minHeight: "600px" }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={t("contact.map.title")}
                  ></iframe>
                </div>
              </motion.div>

              {/* Center: Contact Form */}
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-4"
              >
                <div className="backdrop-blur-sm rounded-2xl p-8 h-full mt-8">
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                    {t("contact.form.title")}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-400 mb-8 text-sm">
                    {t("contact.form.description")}
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-3">
                    <div>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                        placeholder={t("contact.form.fields.name.label")}
                      />
                    </div>

                    <div>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                        placeholder={t("contact.form.fields.email.label")}
                      />
                    </div>

                    <div>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all"
                        placeholder={t("contact.form.fields.phone.label")}
                      />
                    </div>

                    <div>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows="5"
                        className="w-full px-4 py-3.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-dark-700/80 backdrop-blur-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all resize-none"
                        placeholder={t("contact.form.fields.message.label")}
                      ></textarea>
                    </div>

                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="w-full bg-primary-600 text-white py-3.5 rounded-full font-bold hover:bg-primary-700 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                    >
                      {t("contact.form.submitButton")}
                      <span className="text-xl">→</span>
                    </motion.button>
                  </form>
                </div>
              </motion.div>

              {/* Right: Contact Information */}
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="lg:col-span-4"
              >
                <div className="backdrop-blur-sm rounded-2xl p-8 h-full space-y-8 mt-8">
                  {/* Managing Director */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-purple-500 text-white p-3 rounded-lg">
                        <FaUser className="text-xl" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {t("contact.info.director.title")}
                      </h3>
                    </div>
                    <p className="pl-14 text-gray-700 dark:text-gray-300 font-semibold">
                      Nishantha Jayatilaka
                    </p>
                  </div>

                  {/* Address */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-blue-500 text-white p-3 rounded-lg">
                        <FaMapMarkerAlt className="text-xl" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {t("contact.info.address.title")}
                      </h3>
                    </div>
                    <div className="pl-14">
                      <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                        Worga Naturals (Pvt) Ltd.
                        <br />
                        No. 152/G, Meepavita, Udamapitigama
                        <br />
                        Dompe, Sri Lanka (11680)
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-green-500 text-white p-3 rounded-lg">
                        <FaPhone className="text-xl" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {t("contact.info.phone.title")}
                      </h3>
                    </div>
                    <div className="pl-14 space-y-1">
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        {t("contact.info.phone.landline")}:{" "}
                        <a
                          href="tel:+94713992673"
                          className="hover:text-primary-600 transition-colors"
                        >
                          +94 71 399 2673
                        </a>
                      </p>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        {t("contact.info.phone.mobile")}:{" "}
                        <a
                          href="tel:+94773788508"
                          className="hover:text-primary-600 transition-colors"
                        >
                          +94 77 378 8508
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="bg-red-500 text-white p-3 rounded-lg">
                        <FaEnvelope className="text-xl" />
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {t("contact.info.email.title")}
                      </h3>
                    </div>
                    <div className="pl-14 space-y-1">
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        <a
                          href="mailto:info@worganaturals.com"
                          className="hover:text-primary-600 transition-colors"
                        >
                          info@worganaturals.com
                        </a>
                      </p>
                      <p className="text-gray-700 dark:text-gray-300 text-sm">
                        <a
                          href="mailto:nishantha@worganaturals.com"
                          className="hover:text-primary-600 transition-colors"
                        >
                          nishantha@worganaturals.com
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Section 2: Follow Us, Working Hours, QR Code */}
        <section className="py-16 px-4">
          <div className="container mx-auto">
            <div className="grid md:grid-cols-3 gap-6">
              {/* Follow Us */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="backdrop-blur-sm rounded-2xl p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  {t("contact.followUs.title")}
                </h3>
                <div className="flex flex-wrap justify-center gap-4">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={index}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.1, y: -5 }}
                      className={`bg-gray-200 dark:bg-dark-700 text-gray-700 dark:text-gray-300 w-14 h-14 rounded-full flex items-center justify-center transition-all ${social.color} hover:text-white shadow-lg`}
                      title={social.name}
                    >
                      <span className="text-xl">{social.icon}</span>
                    </motion.a>
                  ))}
                </div>
              </motion.div>

              {/* Working Hours */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="backdrop-blur-sm rounded-2xl p-8"
              >
                <div className="flex items-center justify-center gap-3 mb-6">
                  <div className="bg-orange-500 text-white p-3 rounded-lg">
                    <FaClock className="text-2xl" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {t("contact.workingHours.title")}
                  </h3>
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {t("contact.workingHours.weekdays.days")}
                    </span>
                    <span className="text-gray-900 dark:text-white font-bold">
                      {t("contact.workingHours.weekdays.hours")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {t("contact.workingHours.saturday.day")}
                    </span>
                    <span className="text-gray-900 dark:text-white font-bold">
                      {t("contact.workingHours.saturday.hours")}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-gray-700 dark:text-gray-300 font-medium">
                      {t("contact.workingHours.sunday.day")}
                    </span>
                    <span className="text-red-600 dark:text-red-400 font-bold">
                      {t("contact.workingHours.sunday.status")}
                    </span>
                  </div>
                  <p className="text-center text-sm text-gray-600 dark:text-gray-400 pt-3">
                    {t("contact.workingHours.timezone")}
                  </p>
                </div>
              </motion.div>

              {/* Scan Contact Information (QR Code) */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="backdrop-blur-sm rounded-2xl p-8"
              >
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                  {t("contact.qrCode.title")}
                </h3>
                <div className="flex flex-col items-center">
                  <div className="p-4 rounded-xl mb-4">
                    {/* QR Code Image */}
                    <img
                      src="/images/qr-code.png"
                      alt={t("contact.qrCode.alt")}
                      className="w-48 h-48 object-contain rounded-lg"
                    />
                  </div>
                  <p className="text-center text-gray-600 dark:text-gray-400 text-sm">
                    {t("contact.qrCode.description")}
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </div>

      {/* WhatsApp Floating Button */}
      <motion.a
        href="https://wa.me/94773788508"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-8 right-8 bg-green-500 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:bg-green-600 transition-all z-50"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 1, type: "spring" }}
        title={t("contact.whatsapp.title")}
      >
        <FaWhatsapp className="text-3xl" />
      </motion.a>
    </div>
  );
};

export default Contact;
